import { Canvas, useThree } from "@react-three/fiber";
import { useEffect, useMemo } from "react";
import * as THREE from "three";
import { computeLayoutViewRadius } from "../../shared/room-layout";
import type { RoomLayoutRuntime, RoomThemeRuntime } from "../../shared/types";
import { IsometricRoom } from "./IsometricRoom";

interface Props {
  layout: RoomLayoutRuntime;
  theme?: RoomThemeRuntime;
  height?: number;
  zoomMultiplier?: number;
  square?: boolean;
}

function previewZoomForRadius(
  radius: number,
  width: number,
  height: number,
  zoomMultiplier = 1
): number {
  // Scale zoom to preview area and include extra world-space padding so full
  // room bounds stay visible in compact square cards.
  const minViewport = Math.max(96, Math.min(width, height));
  const wallFramePadding = 1.9;
  return THREE.MathUtils.clamp(
    ((minViewport * 0.95) / Math.max(4, radius + wallFramePadding)) *
      zoomMultiplier,
    7,
    68
  );
}

function PreviewCamera({
  viewRadius,
  zoomMultiplier,
}: {
  viewRadius: number;
  zoomMultiplier: number;
}) {
  const { camera, size } = useThree();

  useEffect(() => {
    const cam = camera as THREE.OrthographicCamera;
    const d = Math.max(28, viewRadius * 3.8 + 10);
    cam.position.set(d, d, d);
    cam.lookAt(0, 0, 0);
    cam.zoom = previewZoomForRadius(
      viewRadius,
      size.width,
      size.height,
      zoomMultiplier
    );
    cam.near = 0.1;
    cam.far = 500;
    cam.updateProjectionMatrix();
  }, [camera, viewRadius, zoomMultiplier, size.width, size.height]);

  return null;
}

export function LayoutPreviewCanvas({
  layout,
  theme,
  height = 180,
  zoomMultiplier = 1,
  square = false,
}: Props) {
  const viewRadius = useMemo(() => computeLayoutViewRadius(layout), [layout]);
  const initialSize = square ? 180 : height;

  return (
    <div
      style={{
        width: "100%",
        ...(square ? { aspectRatio: "1 / 1" } : { height }),
        borderRadius: 10,
        overflow: "hidden",
        border: "1px solid rgba(255,255,255,0.08)",
        background: "#05070d",
      }}
    >
      <Canvas
        orthographic
        frameloop="demand"
        dpr={[1, 1.3]}
        camera={{
          position: [44, 44, 44],
          zoom: previewZoomForRadius(
            viewRadius,
            initialSize,
            initialSize,
            zoomMultiplier
          ),
        }}
      >
        <PreviewCamera viewRadius={viewRadius} zoomMultiplier={zoomMultiplier} />
        <ambientLight intensity={0.48} color="#fff8f0" />
        <hemisphereLight intensity={0.35} color="#b8d8f0" groundColor="#e8d4b8" />
        <directionalLight position={[8, 14, 8]} intensity={1.05} color="#fff4e0" />
        <directionalLight position={[3, 6, 9]} intensity={0.22} color="#d0e8ff" />
        <IsometricRoom layout={layout} theme={theme} />
      </Canvas>
    </div>
  );
}
